/* eslint-disable @homeaway/translation-rules/localesCheck */
// @flow
import React, {useCallback, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {Waypoint} from '@homeaway/react-unveil';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import PropertyCarousel from '../PropertyCarousel/PropertyCarousel';
import {trigger as edapTrigger} from '../../utils/edapWrapper';
import {
    triggerPresentedSerpSimilarListingsCarouselEvent,
    triggerSelectedSerpSimilarListingsCarouselCardEvent,
    triggerPresentedSerpSimilarListingsCarouselCardEvent
} from '../../utils/collectionEdapUtils';
import {FLEX_DATES_CAROUSEL} from '../../utils/carouselEdapUtils';
import {DESKTOP_BREAKPOINTS, MOBILE_BREAKPOINTS} from './breakpoints';
import {VRBO_SERP_SEE_MORE_PROMPT} from '../../abacus/experiments';
import Experiment, {Variant} from '@homeaway/react-experiment';
import {SVGIcon} from '@homeaway/react-svg';
import {VIEWED_COLOR__24} from '@homeaway/svg-defs';

import type {PropertyCardListingShape} from '../PropertyCarousel/PropertyCard/PropertyCard';

import './SimilarListingsCarousel.less';

type Props = {
    similarListings: PropertyCardListingShape[],
    isMobile: boolean,
    locale: string
};

const getIndex = ({cardIndex, cardCount, cardsTotal, next}) => {
    const changeAmount = cardCount;
    return next
        ? Math.min(cardsTotal - cardCount, cardIndex + changeAmount)
        : Math.max(0, cardIndex - changeAmount);
};

const SimilarListingsCarousel = ({
    isMobile,
    locale,
    similarListings
}: Props) => {
    const [waypointEntered, setWaypointEntered] = useState(false);
    const breakpoints = isMobile ? MOBILE_BREAKPOINTS : DESKTOP_BREAKPOINTS;
    const [cardIndex, setCardIndex] = useState(0);
    const [cardCount, setCardCount] = useState(1);
    const [cardsTotal, setCardsTotal] = useState(similarListings.length);
    const [showControls, setShowControls] = useState(false);

    const onNextClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: true
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'similar-listings-module',
            event_action: 'next_arrow_clicked'
        });
        edapTrigger('nav.next.click', {
            actionlocation: 'similar-listings-module'
        });
    };
    const onPreviousClick = () => {
        setCardIndex(
            getIndex({
                cardIndex,
                cardCount,
                cardsTotal,
                next: false
            })
        );
        edapTrigger('ux.cards_visible.adjusted', {
            action_location: 'similar-listings-module',
            event_action: 'previous_arrow_clicked'
        });
        edapTrigger('nav.previous.click', {
            actionlocation: 'similar-listings-module'
        });
    };

    const onCarouselResize = ({
        numberCardsVisible,
        numberCardsTotal,
        needsControls
    }) => {
        setCardCount(numberCardsVisible);
        setCardsTotal(numberCardsTotal);
        setShowControls(needsControls);
    };

    const handleWaypointEnter = useCallback(() => {
        if (!waypointEntered) {
            setWaypointEntered(true);
            triggerPresentedSerpSimilarListingsCarouselEvent();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waypointEntered]);

    return (
        <Waypoint onEnter={handleWaypointEnter} useWindowAsScrollParent>
            <div className="SimilarListingsCarousel">
                <div className="PropertyCarousel">
                    <PropertyCarousel
                        isMobile={isMobile}
                        cardIndex={cardIndex}
                        breakpoints={breakpoints}
                        listings={similarListings}
                        onCarouselResize={onCarouselResize}
                        onCardClick={
                            triggerSelectedSerpSimilarListingsCarouselCardEvent
                        }
                        onCardEnter={
                            triggerPresentedSerpSimilarListingsCarouselCardEvent
                        }
                        edapCollectionType={FLEX_DATES_CAROUSEL}
                        title={
                            <div className="PropertyCarousel__header">
                                <div className="PropertyCarousel__header-content">
                                    <h2>
                                        <Experiment
                                            name={VRBO_SERP_SEE_MORE_PROMPT}
                                            deferLogging
                                        >
                                            <Variant variant={1}>
                                                <SVGIcon
                                                    markup={VIEWED_COLOR__24}
                                                    className="SimilarListingsCarousel__icon"
                                                />
                                            </Variant>
                                        </Experiment>
                                        <FormattedMessage
                                            id="seeMorePrompt.carouselTitle"
                                            description="Wonderful! You might like these similar places."
                                            defaultMessage="Wonderful! You might like these similar places."
                                        />
                                    </h2>
                                </div>
                                {!isMobile && (
                                    <div className="PropertyCarousel__header-controls">
                                        <ButtonIcon
                                            label="Previous"
                                            svgMarkup={CHEVRON_LEFT__16}
                                            circle
                                            size="xs"
                                            onClick={onPreviousClick}
                                            disabled={cardIndex === 0}
                                        />
                                        <ButtonIcon
                                            label="Next"
                                            svgMarkup={CHEVRON_RIGHT__16}
                                            circle
                                            size="xs"
                                            onClick={onNextClick}
                                            disabled={
                                                cardIndex ===
                                                    cardsTotal - cardCount ||
                                                !showControls
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        }
                        withImagesCarousel
                        locale={locale}
                    />
                </div>
            </div>
        </Waypoint>
    );
};

export default SimilarListingsCarousel;
